import React from 'react';
import { moduleClientFactory } from '../../api-service/module-client-factory';

export type AiProductKey = 'summa' | 'dialogstoette';

// null = no provider mounted, undefined = permissions still loading.
const AiPermissionsContext = React.createContext<string[] | undefined | null>(null);

const aiSearchClient = moduleClientFactory.createAiSearchClient();

type AiPermissionsProviderProps = {
  children: React.ReactNode;
};

export function AiPermissionsProvider({ children }: AiPermissionsProviderProps) {
  const [permittedAiProducts, setPermittedAiProducts] = React.useState<string[] | undefined>(undefined);

  React.useEffect(() => {
    let isMounted = true;

    async function loadPermittedAiProducts() {
      try {
        const response = await aiSearchClient.getUserApprovedAiProducts();
        if (isMounted) {
          setPermittedAiProducts(response?.approvedAiProducts?.map(product => product.toLocaleLowerCase()) ?? []);
        }
      } catch {
        if (isMounted) {
          setPermittedAiProducts([]);
        }
      }
    }

    loadPermittedAiProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  return <AiPermissionsContext.Provider value={permittedAiProducts}>{children}</AiPermissionsContext.Provider>;
}

export function useHasAiPermission(productKey: AiProductKey): boolean {
  const permittedAiProducts = React.useContext(AiPermissionsContext);
  if (permittedAiProducts === null) {
    throw new Error('useHasAiPermission must be used within AiPermissionsProvider');
  }
  // Fail closed: nothing is permitted until the response has arrived.
  return permittedAiProducts?.includes(productKey) ?? false;
}
